import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { formatCurrency, formatDate, formatTime, cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';

const bookingSchema = z.object({
  playerCount: z.number().min(1).max(4),
  includesCart: z.boolean(),
  players: z.array(z.object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    email: z.string().email().optional().or(z.literal('')),
    handicap: z.number().min(-10).max(54).optional().nullable(),
  })).min(1),
  specialRequests: z.string().optional(),
  promoCode: z.string().optional(),
});

type BookingForm = z.infer<typeof bookingSchema>;

export function BookingPage() {
  const { teeTimeId } = useParams<{ teeTimeId: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const [promoCodeApplied, setPromoCodeApplied] = useState(false);
  const [discount, setDiscount] = useState(0);

  const { data: teeTime, isLoading } = useQuery({
    queryKey: ['tee-time', teeTimeId],
    queryFn: () => api.teeTimes.get(teeTimeId!),
    enabled: !!teeTimeId,
  });

  const { register, handleSubmit, watch, setValue, control, formState: { errors } } = useForm<BookingForm>({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      playerCount: 1,
      includesCart: false,
      players: [{
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: '',
        handicap: user?.handicap || null,
      }],
      specialRequests: '',
      promoCode: '',
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'players',
  });

  const playerCount = watch('playerCount');
  const includesCart = watch('includesCart');
  const promoCode = watch('promoCode');

  // Adjust players array when player count changes
  const handlePlayerCountChange = (count: number) => {
    setValue('playerCount', count);
    const currentPlayers = fields.length;

    if (count > currentPlayers) {
      for (let i = currentPlayers; i < count; i++) {
        append({ firstName: '', lastName: '', email: '', handicap: null });
      }
    } else if (count < currentPlayers) {
      for (let i = currentPlayers - 1; i >= count; i--) {
        remove(i);
      }
    }
  };

  // Calculate pricing
  const basePrice = teeTime?.data?.basePrice || 0;
  const cartPrice = teeTime?.data?.cartPrice || 0;
  const subtotal = (basePrice * playerCount) + (includesCart ? cartPrice * playerCount : 0);
  const taxRate = 0.0825;
  const taxAmount = (subtotal - discount) * taxRate;
  const total = subtotal - discount + taxAmount;

  // Validate promo code
  const validatePromoMutation = useMutation({
    mutationFn: (code: string) => api.promoCodes.validate({
      code,
      applicableTo: 'bookings',
      courseId: teeTime?.data?.courseId,
      amount: subtotal,
    }),
    onSuccess: (result) => {
      if (result.data.isValid) {
        setDiscount(result.data.discountAmount);
        setPromoCodeApplied(true);
        toast.success(`Promo code applied! ${formatCurrency(result.data.discountAmount)} off`);
      } else {
        toast.error(result.data.errorMessage || 'Invalid promo code');
      }
    },
    onError: () => {
      toast.error('Failed to validate promo code');
    },
  });

  // Create booking
  const bookingMutation = useMutation({
    mutationFn: (data: BookingForm) => api.bookings.create({
      teeTimeId: teeTimeId!,
      playerCount: data.playerCount,
      includesCart: data.includesCart,
      players: data.players.map(p => ({
        firstName: p.firstName,
        lastName: p.lastName,
        email: p.email || undefined,
        handicap: p.handicap || undefined,
      })),
      specialRequests: data.specialRequests || undefined,
      promoCode: promoCodeApplied ? data.promoCode : undefined,
    }),
    onSuccess: (result) => {
      toast.success('Booking created successfully!');
      navigate(`/booking/confirmation/${result.data.confirmationCode}`);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create booking');
    },
  });

  if (!isAuthenticated) {
    return (
      <div className="container-narrow py-16 text-center">
        <h1 className="text-2xl font-bold text-sand-900">Sign in to book</h1>
        <p className="mt-4 text-sand-500">You need to be signed in to book a tee time.</p>
        <div className="mt-8 flex justify-center gap-4">
          <Link to={`/login?redirect=/book/${teeTimeId}`}>
            <Button variant="primary">Sign In</Button>
          </Link>
          <Link to={`/register?redirect=/book/${teeTimeId}`}>
            <Button variant="outline">Create Account</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container-narrow py-8">
        <Skeleton className="h-8 w-64 mb-8" />
        <Card>
          <Skeleton className="h-48" />
        </Card>
      </div>
    );
  }

  if (!teeTime?.data) {
    return (
      <div className="container-narrow py-16 text-center">
        <h1 className="text-2xl font-bold text-sand-900">Tee time not found</h1>
        <p className="mt-4 text-sand-500">This tee time may no longer be available.</p>
        <Link to="/tee-times" className="mt-8 inline-block">
          <Button>Browse Available Times</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-sand-50 min-h-screen py-8">
      <div className="container-wide">
        <Link to="/tee-times" className="inline-flex items-center gap-2 text-sm text-sand-500 hover:text-sand-700 mb-6">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to tee times
        </Link>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Booking Form */}
          <div className="lg:col-span-2">
            <Card>
              <h1 className="text-2xl font-bold text-sand-900 mb-6">Complete Your Booking</h1>

              <form onSubmit={handleSubmit((data) => bookingMutation.mutate(data))} className="space-y-8">
                {/* Players */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-sand-900">Players</h2>
                    <Select
                      value={playerCount.toString()}
                      onChange={(e) => handlePlayerCountChange(parseInt(e.target.value))}
                      options={Array.from({ length: Math.min(4, teeTime.data.availableSpots) }, (_, i) => ({
                        value: (i + 1).toString(),
                        label: `${i + 1} Player${i > 0 ? 's' : ''}`,
                      }))}
                      className="w-32"
                    />
                  </div>

                  <div className="space-y-4">
                    {fields.map((field, index) => (
                      <div key={field.id} className="rounded-xl bg-sand-50 p-4">
                        <p className="text-sm font-medium text-sand-700 mb-3">
                          Player {index + 1} {index === 0 && '(You)'}
                        </p>
                        <div className="grid gap-4 sm:grid-cols-2">
                          <Input
                            {...register(`players.${index}.firstName`)}
                            placeholder="First name"
                            error={errors.players?.[index]?.firstName?.message}
                          />
                          <Input
                            {...register(`players.${index}.lastName`)}
                            placeholder="Last name"
                            error={errors.players?.[index]?.lastName?.message}
                          />
                          <Input
                            {...register(`players.${index}.email`)}
                            type="email"
                            placeholder="Email (optional)"
                            error={errors.players?.[index]?.email?.message}
                          />
                          <Input
                            {...register(`players.${index}.handicap`, { valueAsNumber: true })}
                            type="number"
                            placeholder="Handicap (optional)"
                            step="0.1"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Cart Option */}
                <div>
                  <h2 className="text-lg font-semibold text-sand-900 mb-4">Add-ons</h2>
                  <label className="flex items-center gap-4 rounded-xl border border-sand-200 p-4 cursor-pointer hover:bg-sand-50 transition-colors">
                    <input
                      type="checkbox"
                      {...register('includesCart')}
                      className="h-5 w-5 rounded border-sand-300 text-sage-600 focus:ring-sage-500"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-sand-900">Golf Cart</p>
                      <p className="text-sm text-sand-500">Shared cart for your group</p>
                    </div>
                    <p className="font-semibold text-sage-600">
                      +{formatCurrency(cartPrice)} per player
                    </p>
                  </label>
                </div>

                {/* Special Requests */}
                <div>
                  <h2 className="text-lg font-semibold text-sand-900 mb-4">Special Requests</h2>
                  <textarea
                    {...register('specialRequests')}
                    rows={3}
                    placeholder="Any special requests or notes..."
                    className="input resize-none"
                  />
                </div>

                {/* Promo Code */}
                <div>
                  <h2 className="text-lg font-semibold text-sand-900 mb-4">Promo Code</h2>
                  <div className="flex gap-2">
                    <Input
                      {...register('promoCode')}
                      placeholder="Enter code"
                      className="flex-1"
                      disabled={promoCodeApplied}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => promoCode && validatePromoMutation.mutate(promoCode)}
                      disabled={!promoCode || promoCodeApplied || validatePromoMutation.isPending}
                      isLoading={validatePromoMutation.isPending}
                    >
                      {promoCodeApplied ? 'Applied' : 'Apply'}
                    </Button>
                  </div>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  variant="sage"
                  className="w-full"
                  isLoading={bookingMutation.isPending}
                >
                  Complete Booking
                </Button>
              </form>
            </Card>
          </div>

          {/* Order Summary */}
          <div>
            <Card className="sticky top-24">
              <h2 className="text-lg font-semibold text-sand-900 mb-4">Booking Summary</h2>

              {/* Tee Time Details */}
              <div className="rounded-xl bg-sand-50 p-4 mb-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-sand-900">{teeTime.data.course?.name}</p>
                    <p className="text-sm text-sand-500">
                      {teeTime.data.course?.city}, {teeTime.data.course?.state}
                    </p>
                  </div>
                  <Badge variant="sage">{teeTime.data.holes}H</Badge>
                </div>
                <div className="mt-4">
                  <p className="text-2xl font-bold text-sand-900">{formatTime(teeTime.data.time)}</p>
                  <p className="text-sand-500">{formatDate(teeTime.data.date)}</p>
                </div>
              </div>

              {/* Pricing Breakdown */}
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sand-700">
                  <span>Green Fees ({playerCount}x {formatCurrency(basePrice)})</span>
                  <span>{formatCurrency(basePrice * playerCount)}</span>
                </div>
                {includesCart && (
                  <div className="flex justify-between text-sand-700">
                    <span>Golf Cart ({playerCount}x {formatCurrency(cartPrice)})</span>
                    <span>{formatCurrency(cartPrice * playerCount)}</span>
                  </div>
                )}
                {discount > 0 && (
                  <div className="flex justify-between text-sage-600">
                    <span>Discount</span>
                    <span>-{formatCurrency(discount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sand-700">
                  <span>Tax</span>
                  <span>{formatCurrency(taxAmount)}</span>
                </div>
                <hr className="border-sand-200" />
                <div className="flex justify-between text-lg font-semibold text-sand-900">
                  <span>Total</span>
                  <span>{formatCurrency(total)}</span>
                </div>
              </div>

              <p className="text-xs text-sand-400 text-center">
                By completing this booking you agree to our terms and cancellation policy.
              </p>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
